import React, { createContext, useContext, useState, useEffect } from "react";
import { useSetAtom } from "jotai";
import { jwtDecode } from "jwt-decode";
import { ApolloError, useApolloClient } from "@apollo/client";
import { currentUserStateAtom } from "~/state/currentUser";
import { UserRole } from "@modules/authentication/roles";
import { deriveRoleFromClaims } from "@modules/authentication/roleClaims";
import { normalizeAuthToken } from "@modules/authentication/authToken";
import {
  syncSentryUser,
  syncSentryUserFromClaims,
} from "@modules/authentication/sentryUser";
import { MeDocument, MeQuery } from "generated/graphql";

interface AuthContextProps {
  isAuthenticated: boolean;
  loaded: boolean;
  getAuthToken: () => string | null;
  setAuthToken: (token: string | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  loaded: false,
  getAuthToken: () => null,
  setAuthToken: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  issuer?: string;
  children: React.ReactNode;
}

function validateJWT(token: string, issuer?: string) {
  try {
    const decoded = jwtDecode(token);
    if (!decoded || typeof decoded !== "object") {
      return false;
    }

    // If exp is present, check it, otherwise assume it's valid
    if (decoded.exp && decoded.exp < Date.now() / 1000) {
      return false;
    }

    // Check issuer matches
    if (issuer && decoded.iss !== issuer) {
      return false;
    }
  } catch (e) {
    return false;
  }
  return true;
}

function deriveRoleFromJwt(token: string): UserRole | undefined {
  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    return deriveRoleFromClaims(decoded);
  } catch {
    return undefined;
  }
}

function syncSentryUserFromJwt(token: string) {
  try {
    syncSentryUserFromClaims(jwtDecode<Record<string, unknown>>(token));
  } catch {
    syncSentryUser(null);
  }
}

function isUnauthorizedError(error: unknown): boolean {
  if (!(error instanceof ApolloError)) {
    return false;
  }

  const hasAuthGraphQLError = error.graphQLErrors.some((gqlError) => {
    const code = (gqlError.extensions as { code?: string } | undefined)?.code;
    return (
      code === "UNAUTHENTICATED" ||
      code === "FORBIDDEN" ||
      code === "AUTH_NOT_AUTHORIZED" ||
      code === "AUTH_NOT_AUTHENTICATED"
    );
  });
  if (hasAuthGraphQLError) {
    return true;
  }

  const network: any = error.networkError as any;
  const status = network?.statusCode ?? network?.response?.status;
  return status === 401 || status === 403;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  issuer,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const setCurrentUserState = useSetAtom(currentUserStateAtom);
  const apolloClient = useApolloClient();
  const syncRoleCacheFromToken = React.useCallback((token: string) => {
    try {
      localStorage.setItem("role_cache", deriveRoleFromJwt(token) ?? "");
    } catch {
      /* localstorage error */
    }
  }, []);

  const resetCurrentUserState = React.useCallback(() => {
    setCurrentUserState((prev) => ({
      status: "idle",
      requestId: prev.requestId + 1,
    }));
  }, [setCurrentUserState]);

  useEffect(() => {
    let disposed = false;

    const verifyTokenWithServer = async () => {
      try {
        // If the token is stale/invalid (eg. after a deployment), the server will reject this.
        const result = await apolloClient.query<MeQuery>({
          query: MeDocument,
          fetchPolicy: "no-cache",
        });
        if (!disposed) {
          syncSentryUser(result.data?.me);
          setIsAuthenticated(true);
        }
      } catch (e) {
        if (!disposed) {
          // If the server explicitly rejects the token, clear it.
          if (isUnauthorizedError(e)) {
            localStorage.removeItem("authToken");
            try {
              localStorage.setItem("role_cache", "");
              localStorage.setItem("fullname_cache", "");
            } catch {
              /* localstorage error */
            }
          }

          // Either way, don't consider the user authenticated until `me` works.
          syncSentryUser(null);
          setIsAuthenticated(false);
          resetCurrentUserState();
        }
      } finally {
        if (!disposed) {
          setLoaded(true);
        }
      }
    };

    const token = normalizeAuthToken(localStorage.getItem("authToken"));
    if (token && validateJWT(token, issuer)) {
      localStorage.setItem("authToken", token);
      syncRoleCacheFromToken(token);
      // Hold `loaded=false` until we validate with server.
      void verifyTokenWithServer();
    } else {
      syncSentryUser(null);
      setIsAuthenticated(false);
      resetCurrentUserState();
      setLoaded(true);
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== "authToken") {
        return;
      }
      const next = normalizeAuthToken(event.newValue);
      if (next && validateJWT(next, issuer)) {
        localStorage.setItem("authToken", next);
        syncRoleCacheFromToken(next);
        setLoaded(false);
        void verifyTokenWithServer();
      } else {
        syncSentryUser(null);
        setIsAuthenticated(false);
        resetCurrentUserState();
        setLoaded(true);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      disposed = true;
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [apolloClient, issuer, resetCurrentUserState, syncRoleCacheFromToken]);

  const setAuthToken = (rawToken: string | null) => {
    const token = normalizeAuthToken(rawToken);

    if (token && validateJWT(token, issuer)) {
      localStorage.setItem("authToken", token);
      syncRoleCacheFromToken(token);
      syncSentryUserFromJwt(token);
      setIsAuthenticated(true);
      resetCurrentUserState();
    } else {
      localStorage.removeItem("authToken");
      try {
        localStorage.setItem("role_cache", "");
        localStorage.setItem("fullname_cache", "");
      } catch {
        /* localstorage error */
      }
      syncSentryUser(null);
      setIsAuthenticated(false);
      resetCurrentUserState();
    }
  };

  const getAuthToken = () => {
    return normalizeAuthToken(localStorage.getItem("authToken"));
  };

  const logout = () => {
    setAuthToken(null);
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, loaded, setAuthToken, logout, getAuthToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};
